import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Col, Form, InputNumber, message, Modal, Row, Select, Switch, Table, Tag } from "antd";
import { ModalWrapperProps } from "components/Modal/Modal.d";
import errorHandler from "errorHandler";
import {
  Scalars,
  Sensorflow_Ac_Setting_Commands,
  Sensorflow_Ac_Setting_Commands_Insert_Input,
  useCreateAcSettingCommandsMutation,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { validationMessages } from "utils/validation";
import { ACFanSpeed, ACMode, ACStatus, CommandStatus, OperationalMode } from "../../types";

export interface BulkHvacSettingsModalProps {
  selectedRooms: { positionId: string; roomName: string; keyName: string }[];
  onSuccess: () => void;
  // For reopening with existing pending commands
  existingPendingCommands?: CommandWithRoom[];
  onPendingCommandsChange?: (commands: CommandWithRoom[]) => void;
  existingMonitoringStartTime?: number;
  onMonitoringStartTimeChange?: (startTime: number | null) => void;
}

// Define types for monitoring commands
export interface CommandWithRoom {
  id: string; // Command ID
  command: Sensorflow_Ac_Setting_Commands;
  roomName?: string;
  keyName?: string;
  positionId: string;
}

const BulkHvacSettingsModal = ({
  selectedRooms,
  modalWrapperProps,
  closeModal,
  onSuccess,
  existingPendingCommands,
  onPendingCommandsChange,
  existingMonitoringStartTime,
  onMonitoringStartTimeChange,
}: BulkHvacSettingsModalProps & ModalWrapperProps) => {
  const [form] = Form.useForm();
  const [pendingRoomCommands, setPendingRoomCommands] = useState<CommandWithRoom[]>([]);
  const [isMonitoringCommands, setIsMonitoringCommands] = useState(false);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [commandsCompleted, setCommandsCompleted] = useState(0);
  const [totalCommands, setTotalCommands] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastFormValues, setLastFormValues] = useState<any>(null);
  const [monitoringStartTime, setMonitoringStartTime] = useState<number>(0);
  const [remainingTime, setRemainingTime] = useState<number>(60);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Defensive: Maximum number of commands to monitor to prevent UI freeze
  const MAX_COMMANDS = 100;

  // Note: Timeout handling is now managed by the parent component (KeyList)

  // Countdown timer for display purposes only (timeout is managed by parent)
  useEffect(() => {
    if (isMonitoringCommands) {
      // Only set start time if this is a new monitoring session (not a reopened modal)
      let effectiveStartTime = monitoringStartTime;
      if (!effectiveStartTime) {
        effectiveStartTime = Date.now();
        setMonitoringStartTime(effectiveStartTime);
        // Notify parent of the start time
        if (onMonitoringStartTimeChange) {
          onMonitoringStartTimeChange(effectiveStartTime);
        }
      }

      // Calculate current elapsed time and remaining time
      const currentElapsed = Math.floor((Date.now() - effectiveStartTime) / 1000);
      const currentRemaining = Math.max(0, 60 - currentElapsed);
      setRemainingTime(currentRemaining);

      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - effectiveStartTime) / 1000);
        const remaining = Math.max(0, 60 - elapsed);
        setRemainingTime(remaining);

        if (remaining <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          // Stop monitoring when timeout is reached
          setIsMonitoringCommands(false);
        }
      }, 1000);
    } else if (countdownIntervalRef.current) {
      // If monitoring stops, clear the countdown
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    // Cleanup on unmount
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isMonitoringCommands, monitoringStartTime, onMonitoringStartTimeChange]);

  // Note: Command completion is now handled by the parent component

  // Mutation for creating AC setting commands
  const [createAcSettingCommandsMutation, { loading: updateLoading }] = useCreateAcSettingCommandsMutation({
    onCompleted: (data) => {
      const commandsCount = data?.createACSettingCommands?.returning?.length || 0;
      if (commandsCount > 0) {
        // Store commands for monitoring and associate them with position IDs
        const roomCommands: CommandWithRoom[] = [];

        // Map each command to a position ID and attach room/key names
        data?.createACSettingCommands?.returning?.forEach((cmd) => {
          const { positionId } = cmd as any;
          if (!positionId) {
            return;
          }
          const roomInfo = selectedRooms.find((r) => r.positionId === positionId);
          roomCommands.push({
            id: cmd.id,
            command: cmd as Sensorflow_Ac_Setting_Commands,
            positionId,
            roomName: roomInfo?.roomName || "Unknown Room",
            keyName: roomInfo?.keyName || "N/A",
          });
        });

        // Defensive: Limit the number of commands to prevent UI freeze
        if (roomCommands.length > MAX_COMMANDS) {
          message.error(`Too many commands selected (${roomCommands.length}). Please select fewer rooms.`);
          setIsMonitoringCommands(false);
          setShowSummaryView(false);
          setPendingRoomCommands([]);
          setTotalCommands(0);
          return;
        }

        setPendingRoomCommands(roomCommands);
        setTotalCommands(roomCommands.length);
        setIsMonitoringCommands(true);
        setShowSummaryView(true);
        // Reset monitoring start time for new session
        setMonitoringStartTime(0);

        // Notify parent to start monitoring these commands
        if (onPendingCommandsChange) {
          onPendingCommandsChange(roomCommands);
        }

        message.info(`Updating rooms for ${commandsCount} room(s)...`);
      } else {
        message.info("No changes were made to HVAC settings");
        onSuccess();
        closeModal();
      }
    },
    onError: (error) => {
      message.error("Error applying HVAC settings");
      errorHandler.handleError(error);
    },
  });

  // Note: Command status monitoring is now handled by the parent component

  // Note: Command status processing is now handled by the parent component

  // Initialize form with default values and restore pending commands if any
  useEffect(() => {
    // Set default values when the component mounts
    const defaultSetpoint = 24; // Default comfortable temperature

    form.setFieldsValue({
      operationalMode: OperationalMode.Cooling,
      setpoint: defaultSetpoint,
      acStatus: ACStatus.On,
      acMode: ACMode.Cool,
      fanSpeed: ACFanSpeed.Auto,
    });

    // Restore existing pending commands if provided
    if (existingPendingCommands && existingPendingCommands.length > 0) {
      setPendingRoomCommands(existingPendingCommands);
      setTotalCommands(existingPendingCommands.length);
      setShowSummaryView(true);

      // Check if any commands are still pending (not completed)
      const pendingCommandIds = existingPendingCommands
        .filter(
          (cmd) =>
            cmd.command.status !== CommandStatus.SUCCESS &&
            cmd.command.status !== CommandStatus.FAILED &&
            cmd.command.status !== CommandStatus.ERROR
        )
        .map((cmd) => cmd.id);

      if (pendingCommandIds.length > 0) {
        setIsMonitoringCommands(true);
      } else {
        // All commands are completed, just show summary
        setIsMonitoringCommands(false);
      }

      // Restore monitoring start time if provided
      if (existingMonitoringStartTime) {
        setMonitoringStartTime(existingMonitoringStartTime);
      }

      // Update completed count
      const completedCount = existingPendingCommands.filter(
        (cmd) =>
          cmd.command.status === CommandStatus.SUCCESS ||
          cmd.command.status === CommandStatus.FAILED ||
          cmd.command.status === CommandStatus.ERROR
      ).length;
      setCommandsCompleted(completedCount);
    }
  }, [form, existingPendingCommands, existingMonitoringStartTime]);

  // Handle form values changes
  const onFormValuesChange = () => {
    // Update form values as needed
  };

  // Handle save button click
  const handleSave = useCallback(async () => {
    const getACMode = (opMode: OperationalMode): number => {
      return opMode === OperationalMode.Cooling ? ACMode.Cool : ACMode.Heat;
    };
    try {
      const formValues = await form.validateFields();
      setLastFormValues(formValues); // Save for retry
      // Create AC setting commands for each selected position
      const acSettingCommands: Sensorflow_Ac_Setting_Commands_Insert_Input[] = selectedRooms.map(({ positionId }) => ({
        positionId: positionId as Scalars["uuid"],
        source: "DASHBOARD",
        toACStatus: formValues.acStatus,
        toACMode:
          formValues.acMode !== undefined
            ? formValues.acMode
            : getACMode(formValues.operationalMode as OperationalMode),
        toACSetPoint: formValues.setpoint,
        toFanSpeed: formValues.fanSpeed,
        status: "PENDING",
      }));

      await createAcSettingCommandsMutation({
        variables: {
          acSettingCommands,
        },
      });
    } catch (error) {
      // Handle validation errors
      message.error("Please check the form for errors");
    }
  }, [createAcSettingCommandsMutation, selectedRooms, form]);

  // Retry handler
  const handleRetryFailed = useCallback(() => {
    Modal.confirm({
      title: "Retry Failed Rooms?",
      icon: React.createElement(ExclamationCircleOutlined),
      content: `Do you want to retry updating the failed rooms?`,
      okText: "Retry",
      cancelText: "Cancel",
      onOk: async () => {
        setIsRetrying(true);
        try {
          // Use last form values for retry
          const formValues = lastFormValues || (await form.validateFields());
          const getACMode = (opMode: OperationalMode): number => {
            return opMode === OperationalMode.Cooling ? ACMode.Cool : ACMode.Heat;
          };
          // Find failed commands
          const failedCommands = pendingRoomCommands.filter(
            (cmd) => cmd.command.status === CommandStatus.FAILED || cmd.command.status === CommandStatus.ERROR
          );
          if (failedCommands.length === 0) {
            message.info("No failed commands to retry.");
            setIsRetrying(false);
            return;
          }
          // Prepare commands for retry
          const acSettingCommands: Sensorflow_Ac_Setting_Commands_Insert_Input[] = failedCommands.map(
            ({ positionId }) => ({
              positionId: positionId as Scalars["uuid"],
              source: "DASHBOARD",
              toACStatus: formValues.acStatus,
              toACMode:
                formValues.acMode !== undefined
                  ? formValues.acMode
                  : getACMode(formValues.operationalMode as OperationalMode),
              toACSetPoint: formValues.setpoint,
              toFanSpeed: formValues.fanSpeed,
              status: "PENDING",
            })
          );
          await createAcSettingCommandsMutation({
            variables: { acSettingCommands },
          });
          // After mutation, monitoring will restart via onCompleted
        } catch (error) {
          message.error("Failed to retry commands. Please check the form for errors.");
        } finally {
          setIsRetrying(false);
        }
      },
    });
  }, [pendingRoomCommands, lastFormValues, form, createAcSettingCommandsMutation]);

  // Defensive: Only render summary table if not too many commands
  const renderSummaryTable = () => {
    if (pendingRoomCommands.length > MAX_COMMANDS) {
      return <div>Too many commands to display. Please select fewer rooms.</div>;
    }
    const columns = [
      {
        title: "Room",
        dataIndex: "roomName",
        key: "roomName",
        render: (text: string) => text || "Unknown Room",
      },
      {
        title: "Key",
        dataIndex: "keyName",
        key: "keyName",
        render: (text: string) => text || "N/A",
      },
      {
        title: "Status",
        dataIndex: "command",
        key: "status",
        render: ({ status }: Sensorflow_Ac_Setting_Commands) => {
          if (status === CommandStatus.SUCCESS) {
            return <Tag color="success">Success</Tag>;
          }
          if (status === CommandStatus.FAILED || status === CommandStatus.ERROR) {
            return <Tag color="error">Failed</Tag>;
          }
          return <Tag color="warning">Pending</Tag>;
        },
      },
    ];
    const failedCount = pendingRoomCommands.filter(
      (cmd) => cmd.command.status === CommandStatus.FAILED || cmd.command.status === CommandStatus.ERROR
    ).length;
    const hasCommands = pendingRoomCommands.length > 0;

    return (
      <div className="hvac-summary-table">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3>{`${pendingRoomCommands.length} Room command(s) summary`}</h3>
          {isMonitoringCommands && (
            <div style={{ fontSize: "14px", color: "#666" }}>Monitoring timeout: {remainingTime}s remaining</div>
          )}
        </div>
        <Table
          dataSource={pendingRoomCommands}
          columns={columns}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} commands`,
            size: "small",
          }}
          size="small"
        />
        {hasCommands && (
          <Button
            type="primary"
            danger
            loading={isRetrying}
            onClick={handleRetryFailed}
            disabled={isMonitoringCommands || failedCount === 0}
            style={{ marginTop: 16 }}
          >
            Retry Failed Rooms ({failedCount})
          </Button>
        )}
        {/* Debug: Timeout is now managed by parent component */}
      </div>
    );
  };

  // Footer text showing count
  const getFooterCountText = () => {
    if (isMonitoringCommands) {
      return `Updating rooms: ${commandsCompleted}/${totalCommands} completed`;
    }

    if (showSummaryView) {
      return "";
    }

    return `${selectedRooms.length} Room(s) Selected`;
  };

  // Export pending commands when closing during monitoring
  const handleStopMonitoring = () => {
    if (onPendingCommandsChange) {
      onPendingCommandsChange(pendingRoomCommands);
    }
    setIsMonitoringCommands(false);
    setShowSummaryView(true);
  };

  // Button text for cancel/close button
  const getCancelButtonText = () => {
    if (isMonitoringCommands) {
      return "Close";
    }

    return "Cancel";
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    // Always export pending commands before closing if there are any
    if (onPendingCommandsChange && pendingRoomCommands.length > 0) {
      const incompleteCommands = pendingRoomCommands.filter(
        (cmd) =>
          cmd.command.status !== CommandStatus.SUCCESS &&
          cmd.command.status !== CommandStatus.FAILED &&
          cmd.command.status !== CommandStatus.ERROR
      );
      onPendingCommandsChange(incompleteCommands.length > 0 ? pendingRoomCommands : []);
    }

    // Save monitoring start time if commands are still being monitored
    if (onMonitoringStartTimeChange) {
      const hasIncompleteCommands = pendingRoomCommands.some(
        (cmd) =>
          cmd.command.status !== CommandStatus.SUCCESS &&
          cmd.command.status !== CommandStatus.FAILED &&
          cmd.command.status !== CommandStatus.ERROR
      );
      onMonitoringStartTimeChange(hasIncompleteCommands ? monitoringStartTime : null);
    }

    if (isMonitoringCommands) {
      handleStopMonitoring();
    }

    closeModal();
  };

  // Cleanup countdown timer on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <Modal
      title="Bulk Update HVAC Settings"
      centered
      closable={false}
      wrapClassName="bulk-hvac-settings-modal"
      width={450}
      footer={[
        <div key="keys" className="mb-m">
          {getFooterCountText()}
        </div>,
        <Button key="cancel" onClick={handleCancelClick}>
          {getCancelButtonText()}
        </Button>,
        !showSummaryView && (
          <Button
            key="save"
            type="primary"
            loading={updateLoading || isMonitoringCommands}
            onClick={handleSave}
            disabled={isMonitoringCommands}
          >
            Apply to Selected Rooms
          </Button>
        ),
      ].filter(Boolean)}
      {...modalWrapperProps}
    >
      {showSummaryView ? (
        renderSummaryTable()
      ) : (
        <Form
          form={form}
          layout="vertical"
          validateMessages={validationMessages}
          onValuesChange={onFormValuesChange}
          className="compact-form"
        >
          <Row gutter={[16, 8]}>
            {/* AC Status and Temperature */}
            <Col span={24}>
              <Row gutter={[16, 4]}>
                <Col span={12}>
                  {/* AC Status */}
                  <Form.Item
                    label="Power"
                    name="acStatus"
                    valuePropName="checked"
                    getValueFromEvent={(checked) => (checked ? ACStatus.On : ACStatus.Off)}
                  >
                    <Switch checkedChildren="On" unCheckedChildren="Off" defaultChecked />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {/* Setpoint Temperature */}
                  <Form.Item label="Setpoint (°C)" name="setpoint" rules={[{ required: true, message: "Required" }]}>
                    <InputNumber min={16} max={30} precision={1} style={{ width: "100%" }} controls />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            {/* AC Mode and Fan Speed */}
            <Col span={24}>
              <Row gutter={[16, 4]}>
                <Col span={12}>
                  {/* AC Mode */}
                  <Form.Item label="Mode" name="acMode" rules={[{ required: true, message: "Required" }]}>
                    <Select size="middle" style={{ width: "100%" }}>
                      <Select.Option value={ACMode.Auto}>Auto</Select.Option>
                      <Select.Option value={ACMode.Cool}>Cool</Select.Option>
                      <Select.Option value={ACMode.Heat}>Heat</Select.Option>
                      <Select.Option value={ACMode.Dry}>Dry</Select.Option>
                      <Select.Option value={ACMode.Fan}>Fan</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {/* Fan Speed */}
                  <Form.Item label="Fan Speed" name="fanSpeed" rules={[{ required: true, message: "Required" }]}>
                    <Select size="middle" style={{ width: "100%" }}>
                      <Select.Option value={ACFanSpeed.Auto}>Auto</Select.Option>
                      <Select.Option value={ACFanSpeed.Low}>Low</Select.Option>
                      <Select.Option value={ACFanSpeed.Medium}>Medium</Select.Option>
                      <Select.Option value={ACFanSpeed.High}>High</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default BulkHvacSettingsModal;
